import React from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import { HyperLinkNode, BasicNode } from './node-renderers/node-renderer';
import { NodeSelectedHandler, NodeUpdatedHandler, NodeDeletedHandler } from './event-handlers/event-handler';
import { NodeOperations, DefaultTreeViewClassName, DefaultNodePaddingLeft, DefaultCellHeight } from './constants';
import { TreeViewProps } from './shapes';
import { VirtualizedTree } from './react-virtualized-tree';

let prevSearchValue = '';

export class TreeView extends React.Component {
  componentDidMount = () => {
    this.props.onTreeViewDidMount();
  };

  componentWillUnmount = () => {
    this.props.onTreeViewWillUnmount();
  };

  get updateTypeHandlers() {
    const defaultTypeHandlers = {
      [NodeOperations.ADDED]: NodeUpdatedHandler,
      [NodeOperations.UPDATED]: NodeUpdatedHandler,
      [NodeOperations.DELETED]: NodeDeletedHandler,
      [NodeOperations.EXPANDED]: NodeUpdatedHandler,
      [NodeOperations.COLLAPSED]: NodeUpdatedHandler,
      [NodeOperations.SELECTED]: NodeSelectedHandler,
    };

    return _.extend(defaultTypeHandlers, this.props.updateTypeHandlers);
  }

  getSearchChange = () => {
    const { currentSearchValue } = this.props;
    let returnVar = false;
    if (currentSearchValue !== prevSearchValue) {
      prevSearchValue = currentSearchValue;
      returnVar = true;
    }
    return returnVar;
  };

  renderNode = (node, onChange) => {
    const nodeProps = {
      onChange,
      nodeChildRenderer: this.props.nodeChildRenderer,
      iconsClassNameMap: this.props.iconsClassNameMap,
      getClassNames: this.props.getClassNames,
      expandIconAriaLabel: this.props.expandIconAriaLabel,
    };

    return this.props.nodeRenderer(node, nodeProps);
  };

  rendererWrap = ({ node, onChange }) => this.renderNode(node, onChange);

  render() {
    return (
      <VirtualizedTree
        ariaLabel={this.props.ariaLabel}
        nodes={this.props.nodes}
        extensions={{
          updateTypeHandlers: this.updateTypeHandlers,
        }}
        onChange={this.props.onChange}
        filterNode={this.props.filterNode}
        filterNodeDeep={this.props.filterNodeDeep}
        isSearch={this.getSearchChange()}
        nodePaddingLeft={this.props.nodePaddingLeft}
        cellHeight={this.props.cellHeight}
        enableOnScrollLoad={this.props.enableOnScrollLoad}
        onScrollOptions={this.props.onScrollOptions}
        scrollToId={this.props.scrollToId}
        rowCount={this.props.rowCount}
        getFlattenedTree={this.props.getFlattenedTree}
        isRowLoaded={this.props.isRowLoaded}
        loadMoreRows={this.props.loadMoreRows}
        maxHeight={this.props.maxHeight}
        autoHeight={this.props.autoHeight}
        getClassNames={this.props.getClassNames}
        treeViewClassNames={this.props.treeViewClassNames}
        shouldClearCellMeasurerCache={this.props.shouldClearCellMeasurerCache}
        placeholder={this.props.placeholder}
      >
        {
          this.props.optimizeRendering ? this.rendererWrap :
          ({ node, onChange }) => this.renderNode(node, onChange)
        }
      </VirtualizedTree>
    );
  }
}

TreeView.propTypes = TreeViewProps;

TreeView.defaultProps = {
  nodeRenderer: HyperLinkNode,
  nodeChildRenderer: BasicNode,
  updateTypeHandlers: {},
  filterNode: node => !_.isNull(node),
  filterNodeDeep: false,
  currentSearchValue: '',
  onTreeViewWillUnmount: () => _.noop,
  onTreeViewDidMount: () => _.noop,
  iconsClassNameMap: {},
  getClassNames: () => [],
  treeViewClassNames: DefaultTreeViewClassName,
  nodePaddingLeft: DefaultNodePaddingLeft,
  cellHeight: DefaultCellHeight,
  autoHeight: false,
  maxHeight: null,
  scrollToId: null,
  enableOnScrollLoad: false,
  rowCount: undefined,
  getFlattenedTree: undefined,
  isRowLoaded: () => true,
  loadMoreRows: () => Promise.resolve({}),
  onScrollOptions: {
    threshold: 15,
    batchSizeLimit: 15,
  },
  expandIconAriaLabel: undefined,
  ariaLabel: undefined,

  // eslint-disable-next-line max-len
  // There is to prevent new copy of renderNode. Currently it follows react antipattern which causes child node constructor to be called on every render.
  // For MAE, converting anonymous funciton to named function gave perf gain of 30 to 40%.
  optimizeRendering: false,
  shouldClearCellMeasurerCache: false,
  placeholder: null,
};
